import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import { SeoHead } from "@/components/common/SeoHead";
import { WhatsAppCTA } from "@/components/common/WhatsAppCTA";
import { AnimatedSection } from "@/components/common/AnimatedSection";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Check, MessageCircle, Search, Plane, HeadphonesIcon } from "lucide-react";

const BREADCRUMB = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://tripsyfy.com" },
    { "@type": "ListItem", position: 2, name: "How It Works", item: "https://tripsyfy.com/how-it-works" },
  ],
});

const FAQ_SCHEMA = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "How do I start planning a trip with Tripsyfy?", acceptedAnswer: { "@type": "Answer", text: "Simply contact us by phone (+1 972-666-1122), WhatsApp, email (hello@tripsyfy.com), or by filling out the quote request form on our website. Share your destination, travel dates, number of travelers, and any preferences — we'll take it from there." } },
    { "@type": "Question", name: "How long does it take to get a quote?", acceptedAnswer: { "@type": "Answer", text: "We typically respond to quote requests within 24 business hours. For urgent travel needs or simple itineraries, we can often turn around options within a few hours, especially via WhatsApp." } },
    { "@type": "Question", name: "Do I need to know exactly where I want to go?", acceptedAnswer: { "@type": "Answer", text: "Not at all. Many clients come to us with a general idea — a budget, a region, or a travel style — and we help them decide on the specific destination and itinerary. We're happy to guide the planning from the very beginning." } },
    { "@type": "Question", name: "What details should I send when requesting a quote?", acceptedAnswer: { "@type": "Answer", text: "The more detail the better, but the essentials are: departure city, destination (or preferred region), travel dates (or flexible date range), number of travelers (adults and children), type of trip (flight only, tour, cruise, full package), and any special requirements like dietary needs, accessibility, or preferences." } },
    { "@type": "Question", name: "Can Tripsyfy help with complex multi-city trips?", acceptedAnswer: { "@type": "Answer", text: "Yes. Multi-city itineraries, open-jaw flights, and complex routes with multiple destinations are exactly the kind of trips we specialize in. Online booking tools often miss cheaper or more practical routing options — our team finds them for you." } },
    { "@type": "Question", name: "What happens after I approve a quote?", acceptedAnswer: { "@type": "Answer", text: "Once you approve the quote and share the booking details, we proceed with the reservation and provide you with all confirmation details, e-ticket numbers, and a full trip summary. We remain available for any changes, questions, or support until your trip is complete." } },
    { "@type": "Question", name: "Is the Tripsyfy service free?", acceptedAnswer: { "@type": "Answer", text: "Quote requests and consultations are free. Our fees for booking assistance are either included in the service rate or disclosed transparently before you commit. There are no surprise charges." } },
  ],
});

const steps = [
  {
    number: "01",
    icon: MessageCircle,
    title: "Share your travel details",
    description: "Tell us where you want to go, when, how many travelers, and your budget. You can reach us by phone, WhatsApp, or the quote form. No need to have everything figured out — we'll help with that.",
  },
  {
    number: "02",
    icon: Search,
    title: "We compare practical options",
    description: "Our team reviews available flights, tours, cruises, or packages that match your needs. We compare prices, routing, timing, and value — not just the cheapest fare, but the right option for your trip.",
  },
  {
    number: "03",
    icon: Check,
    title: "You choose what works",
    description: "We present you with clear options in plain language. You decide what fits your schedule, budget, and preferences. No pressure, no upselling — just straightforward guidance.",
  },
  {
    number: "04",
    icon: HeadphonesIcon,
    title: "We book it and stay with you",
    description: "Once you're happy, we handle the booking and send you all the confirmation details. We remain available for changes, support, and questions — from the day you book until you're back home.",
  },
];

const checklist = [
  "Departure city (which airport you're flying from)",
  "Destination or region (country, city, or area)",
  "Preferred travel dates (or a flexible date range)",
  "Number of travelers (adults, children, and their ages)",
  "Trip type: flights only, tour, cruise, or full package",
  "Cabin class preference (economy, business, first)",
  "Whether dates are flexible and by how much",
  "Budget range (if known)",
  "Any special needs: dietary, mobility, extra baggage",
  "Accommodation preferences (if booking a full package)",
];

const faqs = [
  { q: "How do I start planning a trip with Tripsyfy?", a: "Simply contact us by phone (+1 972-666-1122), WhatsApp, email, or by filling out the quote request form. Share your destination, travel dates, number of travelers, and any preferences — we'll take it from there." },
  { q: "How long does it take to get a quote?", a: "We typically respond within 24 business hours. For urgent needs or simple itineraries, we can often respond within a few hours via WhatsApp." },
  { q: "Do I need to know exactly where I want to go?", a: "Not at all. Many clients come to us with just a general idea or a budget — we help them decide on the destination and itinerary from the beginning." },
  { q: "What details should I send when requesting a quote?", a: "The essentials: departure city, destination, travel dates, number of travelers (adults and children), trip type (flight, tour, cruise, full package), and any special requirements." },
  { q: "Can Tripsyfy help with complex multi-city trips?", a: "Yes. Multi-city itineraries, open-jaw flights, and complex routes are exactly what we specialize in. Online tools often miss better routing options — we find them for you." },
  { q: "What happens after I approve a quote?", a: "We proceed with the reservation and provide all confirmation details, e-ticket numbers, and a full trip summary. We remain available for changes and support until your trip is complete." },
  { q: "Is the Tripsyfy service free?", a: "Quote requests and consultations are free. Any booking fees are disclosed transparently before you commit. No surprise charges." },
];

export default function HowItWorksPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div id="main-content" className="min-h-screen bg-white">
      <Helmet>
        <script type="application/ld+json">{BREADCRUMB}</script>
        <script type="application/ld+json">{FAQ_SCHEMA}</script>
      </Helmet>
      <SeoHead
        title="How Tripsyfy Works | Travel Planning Made Simple"
        description="Learn how Tripsyfy simplifies travel planning — send your details, we compare options, you choose, we help you book. Human support at every step."
        canonical="https://tripsyfy.com/how-it-works"
      />
      <Navbar />

      <section className="relative pt-28 pb-14 bg-primary">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <p className="text-sky-300 text-xs font-semibold tracking-widest uppercase mb-3">Simple Process</p>
          <h1 className="heading-xl text-white mb-3">How Tripsyfy Works</h1>
          <p className="text-base text-white/70 max-w-xl leading-relaxed">
            Four steps from "I want to travel" to a confirmed booking — with a real person helping you every step of the way.
          </p>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {steps.map(({ number, icon: Icon, title, description }, i) => (
              <AnimatedSection key={number} delay={i * 0.08}>
                <div className="flex flex-col h-full">
                  <div className="h-12 w-12 rounded-2xl bg-accent/10 flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6 text-accent" />
                  </div>
                  <p className="text-4xl font-bold text-primary/10 mb-2 leading-none">{number}</p>
                  <h2 className="text-base font-bold text-primary mb-3">{title}</h2>
                  <p className="text-sm text-muted-foreground leading-relaxed flex-1">{description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-surface-subtle">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <AnimatedSection>
              <p className="overline mb-3">Step 1 — Travel Details</p>
              <h2 className="heading-lg text-primary mb-5">What to send us when you reach out</h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                You don't need everything sorted before contacting us. But the more detail you can share, the faster we can get you accurate options. Here's the ideal checklist:
              </p>
              <ul className="space-y-3">
                {checklist.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="h-5 w-5 rounded-full bg-accent/10 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="h-3 w-3 text-accent" />
                    </div>
                    <span className="text-sm text-foreground leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </AnimatedSection>
            <AnimatedSection delay={0.1}>
              <div className="bg-white rounded-xl shadow-card p-8">
                <p className="overline mb-3">Quick Start</p>
                <h3 className="heading-md text-primary mb-4">Start planning your trip now</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                  The fastest way to get started is to message us on WhatsApp or call us directly. Tell us where you want to go and we'll take it from there.
                </p>
                <div className="space-y-3">
                  <WhatsAppCTA variant="banner" />
                  <Link to="/contact">
                    <Button variant="outline" className="w-full mt-3">Send a Quote Request</Button>
                  </Link>
                </div>
                <div className="mt-5 pt-5 border-t border-border">
                  <p className="text-xs text-muted-foreground text-center">
                    Or call us: <a href="tel:+19726661122" className="text-accent font-semibold hover:underline">+1 972 666 1122</a>
                  </p>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-wide px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto">
          <AnimatedSection>
            <p className="overline mb-3 text-center">Common Questions</p>
            <h2 className="heading-lg text-primary text-center mb-10">How the process works</h2>
          </AnimatedSection>
          <div className="space-y-3">
            {faqs.map(({ q, a }, i) => (
              <AnimatedSection key={i} delay={i * 0.04}>
                <details className="bg-surface-subtle rounded-xl overflow-hidden">
                  <summary className="px-6 py-4 cursor-pointer font-semibold text-primary text-sm list-none flex items-center justify-between gap-4 hover:text-accent transition-colors">
                    {q}
                    <span className="text-accent text-lg leading-none shrink-0">+</span>
                  </summary>
                  <div className="px-6 pb-5">
                    <p className="text-sm text-muted-foreground leading-relaxed">{a}</p>
                  </div>
                </details>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center max-w-2xl mx-auto mb-10">
              <p className="overline mb-3">Our Services</p>
              <h2 className="heading-lg text-primary">What Tripsyfy can plan for you</h2>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { title: "International Flight Booking", path: "/international-flight-booking", desc: "Complex routes, families, groups, and hard-to-book itineraries." },
              { title: "Flights to India from USA", path: "/flights-to-india-from-usa", desc: "Diaspora families, students, and festival season travelers." },
              { title: "Flights to Africa from USA", path: "/flights-to-africa-from-usa", desc: "Nigeria, Ghana, Kenya, South Africa, and more." },
              { title: "Custom Tour Packages", path: "/custom-tour-packages", desc: "Itineraries built around your destination, budget, and style." },
              { title: "Group Travel Planning", path: "/group-travel-planning", desc: "Flights, hotels, and logistics for groups of 5 or more." },
              { title: "Cruise Booking Assistance", path: "/cruise-booking-assistance", desc: "Caribbean, Mediterranean, Alaska, and polar expeditions." },
            ].map(({ title, path, desc }, i) => (
              <AnimatedSection key={path} delay={i * 0.05}>
                <Link to={path} className="group bg-surface-subtle rounded-xl p-5 flex flex-col gap-2 hover:shadow-card-hover transition-shadow duration-200">
                  <p className="text-sm font-bold text-primary group-hover:text-accent transition-colors">{title}</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">{desc}</p>
                </Link>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-primary">
        <div className="container-wide px-4 sm:px-6 lg:px-8 max-w-2xl mx-auto text-center">
          <AnimatedSection>
            <p className="text-xs font-semibold tracking-widest uppercase text-accent mb-3">Ready to Travel?</p>
            <h2 className="heading-lg text-white mb-4">Let's start planning your trip</h2>
            <p className="text-white/70 text-base leading-relaxed mb-8">
              Reach us by WhatsApp, phone, or the contact form — and we'll get started right away.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/contact"><Button className="bg-accent text-white hover:bg-accent/90 w-full sm:w-auto">Request a Free Quote</Button></Link>
              <WhatsAppCTA variant="button" />
            </div>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
    </div>
  );
}
